import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import type { Profile, VerificationRequest } from '@/lib/database.types';
import { fetchProfilesByIds } from '@/lib/agreements';
import { formatDateTime } from '@/lib/format';
import {
  getVerificationDocUrl,
  listPendingVerifications,
  reviewVerification,
} from '@/lib/verification';
import {
  deleteIdentityDocuments,
  getIdentityPhotoUrl,
  getVerificationAuditLog,
  listPendingIdentityVerifications,
  reviewIdentityVerification,
} from '@/lib/identityVerification';
import type { IdentityVerification, VerificationAuditEntry } from '@/lib/identityVerification';
import { ErrorText } from '@/components/ErrorText';

type Tab = 'phone' | 'identity' | 'history';

const REJECTION_TAG_KEYS = [
  { key: 'blurry', i18nKey: 'adminScreen.tagBlurry' },
  { key: 'dark', i18nKey: 'adminScreen.tagDark' },
  { key: 'invalid_doc', i18nKey: 'adminScreen.tagInvalidDoc' },
] as const;
type RejectionTag = typeof REJECTION_TAG_KEYS[number]['key'];

export default function AdminVerificationsScreen() {
  const { t } = useTranslation();
  const REJECTION_TAGS = useMemo(
    () => REJECTION_TAG_KEYS.map((tag) => ({ key: tag.key, label: t(tag.i18nKey) })),
    [t]
  );
  const { profile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [tab, setTab] = useState<Tab>('identity');

  // Phone verifications
  const [requests, setRequests] = useState<VerificationRequest[]>([]);
  const [phoneProfiles, setPhoneProfiles] = useState<Record<string, Profile>>({});
  const [phoneBusy, setPhoneBusy] = useState<string | null>(null);

  // Identity verifications
  const [idvRequests, setIdvRequests] = useState<IdentityVerification[]>([]);
  const [idvProfiles, setIdvProfiles] = useState<Record<string, Profile>>({});
  const [idvBusy, setIdvBusy] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<IdentityVerification | null>(null);
  const [selectedTags, setSelectedTags] = useState<Set<RejectionTag>>(new Set());
  const [nameEdits, setNameEdits] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ιστορικό επαληθεύσεων
  const [auditLog, setAuditLog] = useState<VerificationAuditEntry[]>([]);

  const isAdmin = !!profile?.is_admin;

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setError(null);
    try {
      const [phoneList, idvList, audit] = await Promise.all([
        listPendingVerifications(),
        listPendingIdentityVerifications(),
        getVerificationAuditLog(100).catch(() => []),
      ]);
      setRequests(phoneList);
      setIdvRequests(idvList);
      setAuditLog(audit);
      const allIds = [...phoneList.map((r) => r.user_id), ...idvList.map((r) => r.user_id)];
      const profs = await fetchProfilesByIds(allIds);
      setPhoneProfiles(profs);
      setIdvProfiles(profs);
    } catch (e) {
      setError(getErrorMessage(e, t('adminScreen.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  async function viewDoc(path: string) {
    try {
      const url = await getVerificationDocUrl(path);
      await WebBrowser.openBrowserAsync(url);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('adminScreen.docOpenFailed')));
    }
  }

  async function viewIdvPhoto(path: string) {
    try {
      const url = await getIdentityPhotoUrl(path);
      await WebBrowser.openBrowserAsync(url);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('adminScreen.photoOpenFailed')));
    }
  }

  async function decidePhone(req: VerificationRequest, approve: boolean) {
    setPhoneBusy(req.id);
    try {
      await reviewVerification(req.id, approve);
      setRequests((rs) => rs.filter((r) => r.id !== req.id));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('adminScreen.actionFailed')));
    } finally {
      setPhoneBusy(null);
    }
  }

  async function decideIdv(req: IdentityVerification, approve: boolean, rejectionReason?: string) {
    setIdvBusy(req.id);
    try {
      const correctedName = approve ? nameEdits[req.id]?.trim() : undefined;
      await reviewIdentityVerification(req.id, approve, rejectionReason, correctedName);
      // GDPR cleanup: τα έγγραφα ταυτότητας δεν κρατούνται μετά τον έλεγχο, είτε εγκρίθηκε είτε απορρίφθηκε.
      try {
        await deleteIdentityDocuments(req);
      } catch (cleanupError) {
        console.error('[decideIdv] cleanup failed', cleanupError);
      }
      setIdvRequests((rs) => rs.filter((r) => r.id !== req.id));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('adminScreen.actionFailed')));
    } finally {
      setIdvBusy(null);
    }
  }

  function openRejectModal(req: IdentityVerification) {
    setRejectTarget(req);
    setSelectedTags(new Set());
  }

  function toggleTag(tag: RejectionTag) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    const reason = selectedTags.size > 0
      ? REJECTION_TAGS.filter((t) => selectedTags.has(t.key)).map((t) => t.label).join(', ')
      : undefined;
    setRejectTarget(null);
    await decideIdv(rejectTarget, false, reason);
  }

  if (!isAdmin) {
    return (
      <View style={[styles.loader, { padding: spacing.lg, gap: spacing.md }]}>
        <Text style={styles.empty}>{t('adminScreen.noAccess')}</Text>
        <Button title={t('common.back')} variant="secondary" onPress={() => router.back()} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('adminScreen.verificationsTitle')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.adminLinksRow}>
        <Pressable
          onPress={() => router.push('/(app)/admin/manage-admins')}
          style={[styles.adminLink, { flex: 1 }]}
        >
          <Text style={styles.adminLinkText}>{t('adminScreen.manageAdminsLink')}</Text>
        </Pressable>
        <Pressable
          onPress={() => router.push('/(app)/admin/feedback' as any)}
          style={[styles.adminLink, { flex: 1 }]}
        >
          <Text style={styles.adminLinkText}>{t('adminScreen.feedbackLink')}</Text>
        </Pressable>
        <Pressable
          onPress={() => router.push('/(app)/admin/chat-reports' as any)}
          style={[styles.adminLink, { flex: 1 }]}
        >
          <Text style={styles.adminLinkText}>{t('adminScreen.chatReportsLink')}</Text>
        </Pressable>
      </View>

      {/* ── Tabs ── */}
      <View style={styles.tabs}>
        <Pressable
          style={[styles.tab, tab === 'identity' && styles.tabActive]}
          onPress={() => setTab('identity')}
          accessibilityRole="tab"
        >
          <Text style={[styles.tabText, tab === 'identity' && styles.tabTextActive]}>
            {t('adminScreen.tabIdentity')}
            {idvRequests.length > 0 && <Text style={styles.tabBadge}> ({idvRequests.length})</Text>}
          </Text>
        </Pressable>
        <Pressable
          style={[styles.tab, tab === 'phone' && styles.tabActive]}
          onPress={() => setTab('phone')}
          accessibilityRole="tab"
        >
          <Text style={[styles.tabText, tab === 'phone' && styles.tabTextActive]}>
            {t('adminScreen.tabPhone')}
            {requests.length > 0 && <Text style={styles.tabBadge}> ({requests.length})</Text>}
          </Text>
        </Pressable>
        <Pressable
          style={[styles.tab, tab === 'history' && styles.tabActive]}
          onPress={() => setTab('history')}
          accessibilityRole="tab"
        >
          <Text style={[styles.tabText, tab === 'history' && styles.tabTextActive]}>
            {t('adminScreen.tabHistory')}
          </Text>
        </Pressable>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : error ? (
        <ErrorText>{error}</ErrorText>
      ) : tab === 'identity' ? (
        idvRequests.length === 0 ? (
          <Text style={styles.empty}>{t('adminScreen.emptyIdentity')}</Text>
        ) : (
          idvRequests.map((req) => {
            const name = idvProfiles[req.user_id]?.full_name?.trim() || t('adminScreen.defaultUser');
            const nameValue = nameEdits[req.id] ?? name;
            return (
              <View key={req.id} style={styles.card}>
                <Text style={styles.meta}>{formatDateTime(req.created_at)}</Text>
                <Text style={styles.nameFieldLabel}>{t('adminScreen.nameFieldLabel')}</Text>
                <TextInput
                  style={styles.nameField}
                  value={nameValue}
                  onChangeText={(v) => setNameEdits((prev) => ({ ...prev, [req.id]: v }))}
                  placeholder={t('adminScreen.nameFieldPlaceholder')}
                  placeholderTextColor={colors.textMuted}
                />
                <Text style={styles.nameFieldHint}>
                  {t('adminScreen.nameFieldHint')}
                </Text>
                <View style={styles.photoButtons}>
                  <View style={styles.photoBtn}>
                    <Button
                      title={t('adminScreen.photoFront')}
                      icon="card-outline"
                      variant="secondary"
                      onPress={() => void viewIdvPhoto(req.front_id_path)}
                    />
                  </View>
                  <View style={styles.photoBtn}>
                    <Button
                      title={t('adminScreen.photoBack')}
                      icon="card-outline"
                      variant="secondary"
                      onPress={() => void viewIdvPhoto(req.back_id_path)}
                    />
                  </View>
                  <View style={styles.photoBtn}>
                    <Button
                      title={t('adminScreen.photoSelfie')}
                      icon="person-outline"
                      variant="secondary"
                      onPress={() => void viewIdvPhoto(req.selfie_path)}
                    />
                  </View>
                </View>
                <View style={styles.actions}>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('adminScreen.reviewed')}
                      icon="checkmark-circle-outline"
                      loading={idvBusy === req.id}
                      onPress={() => void decideIdv(req, true)}
                    />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('adminScreen.reject')}
                      icon="close-circle-outline"
                      variant="secondary"
                      danger
                      loading={idvBusy === req.id}
                      onPress={() => openRejectModal(req)}
                    />
                  </View>
                </View>
              </View>
            );
          })
        )
      ) : tab === 'phone' ? (
        requests.length === 0 ? (
          <Text style={styles.empty}>{t('adminScreen.emptyPhone')}</Text>
        ) : (
          requests.map((req) => {
            const name = phoneProfiles[req.user_id]?.full_name?.trim() || t('adminScreen.defaultUser');
            return (
              <View key={req.id} style={styles.card}>
                <Text style={styles.name}>{name}</Text>
                <Text style={styles.meta}>
                  {req.doc_type === 'passport' ? t('adminScreen.docTypePassport') : t('adminScreen.docTypeId')} ·{' '}
                  {formatDateTime(req.created_at)}
                </Text>
                <Button
                  title={t('adminScreen.viewDocument')}
                  icon="document-outline"
                  variant="secondary"
                  onPress={() => void viewDoc(req.document_path)}
                />
                <View style={styles.actions}>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('adminScreen.approve')}
                      icon="checkmark-circle-outline"
                      loading={phoneBusy === req.id}
                      onPress={() => void decidePhone(req, true)}
                    />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('adminScreen.rejectSimple')}
                      icon="close-circle-outline"
                      variant="secondary"
                      danger
                      loading={phoneBusy === req.id}
                      onPress={() => void decidePhone(req, false)}
                    />
                  </View>
                </View>
              </View>
            );
          })
        )
      ) : (
        auditLog.length === 0 ? (
          <Text style={styles.empty}>{t('adminScreen.emptyHistory')}</Text>
        ) : (
          auditLog.map((entry) => (
            <View key={entry.id} style={styles.card}>
              <Text style={styles.name}>{entry.user_name?.trim() || t('adminScreen.defaultUser')}</Text>
              <Text style={styles.meta}>
                {entry.kind === 'phone' ? t('adminScreen.tabPhone') : t('adminScreen.tabIdentity')}
                {' · '}
                {t(`adminScreen.auditAction_${entry.action}`)}
                {' · '}
                {formatDateTime(entry.created_at)}
              </Text>
              <Text style={styles.meta}>
                {entry.reviewer_name ? t('adminScreen.auditBy', { name: entry.reviewer_name }) : t('adminScreen.auditBySelf')}
              </Text>
              {!!entry.note && <Text style={styles.meta}>{entry.note}</Text>}
            </View>
          ))
        )
      )}

      {/* ── Modal επιλογής λόγου απόρριψης ── */}
      <Modal
        visible={!!rejectTarget}
        transparent
        animationType="fade"
        onRequestClose={() => setRejectTarget(null)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setRejectTarget(null)}>
          <Pressable style={styles.modalSheet} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('adminScreen.rejectModalTitle')}</Text>
            <Text style={styles.modalSub}>{t('adminScreen.rejectModalSubtitle')}</Text>
            <View style={styles.tagRow}>
              {REJECTION_TAGS.map((tag) => {
                const active = selectedTags.has(tag.key);
                return (
                  <Pressable
                    key={tag.key}
                    style={[styles.tag, active && styles.tagActive]}
                    onPress={() => toggleTag(tag.key)}
                    accessibilityRole="checkbox"
                  >
                    <Text style={[styles.tagText, active && styles.tagTextActive]}>{tag.label}</Text>
                  </Pressable>
                );
              })}
            </View>
            <View style={styles.modalActions}>
              <View style={styles.actionBtn}>
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setRejectTarget(null)} />
              </View>
              <View style={styles.actionBtn}>
                <Button title={t('adminScreen.reject')} danger onPress={() => void confirmReject()} loading={idvBusy === rejectTarget?.id} />
              </View>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    loader: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background,
    },
    content: { paddingHorizontal: spacing.lg, gap: spacing.md },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.sm,
    },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600', width: 70 },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 70 },
    adminLinksRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    adminLink: {
      minWidth: 100,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
    },
    adminLinkText: { fontSize: fontSize.sm, fontWeight: '600', color: colors.primary },
    tabs: {
      flexDirection: 'row',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
    },
    tab: {
      flex: 1,
      paddingVertical: spacing.sm,
      alignItems: 'center',
      backgroundColor: colors.surfaceAlt,
    },
    tabActive: { backgroundColor: colors.primary },
    tabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    tabTextActive: { color: colors.textOnPrimary },
    tabBadge: { fontWeight: '700' },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    name: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    meta: { color: colors.textMuted, fontSize: fontSize.sm },
    nameFieldLabel: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    nameField: {
      height: 46,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceAlt,
      paddingHorizontal: spacing.md,
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    nameFieldHint: { color: colors.textMuted, fontSize: 11, lineHeight: 15 },
    photoButtons: { flexDirection: 'row', gap: spacing.xs },
    photoBtn: { flex: 1 },
    actions: { flexDirection: 'row', gap: spacing.sm },
    actionBtn: { flex: 1 },
    empty: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      paddingVertical: spacing.xl,
    },
    error: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center' },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.lg,
    },
    modalSheet: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      width: '100%',
      gap: spacing.md,
      ...shadow.card,
    },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '800' },
    modalSub: { color: colors.textMuted, fontSize: fontSize.sm },
    tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    tag: {
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
      borderRadius: radius.full,
      borderWidth: 1.5,
      borderColor: colors.border,
      backgroundColor: colors.surfaceAlt,
    },
    tagActive: { borderColor: colors.danger, backgroundColor: `${colors.danger}18` },
    tagText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    tagTextActive: { color: colors.danger },
    modalActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  });
}
